import { ComputedRef, Ref, computed } from "vue";

import type { EventDrilldown } from "@/shared/model/event";
import { Side } from "@/shared/model/enums/side";
import { MediaType } from "@/shared/model/enums/media-type";

import {
    DRILLDOWN_COUNTRY_LEVEL_NUMBER,
    DRILLDOWN_LEAGUE_LEVEL_NUMBER,
    DRILLDOWN_SPORT_LEVEL_NUMBER,
} from "@/shared/utils/constants";
import { DrilldownNode } from "../model/drilldown-node";
import { Sport } from "../model/enums/sport";
import { computedAsync } from "@vueuse/core";
import { useDrilldownNodesStore } from "../stores/use-drilldown-nodes-store";
export default function useEventDetails<T extends EventDrilldown>(event: Ref<T>) {
    const name: ComputedRef<string> = computed(() => {
        if (!event?.value) return "";

        return event.value.name;
    });

    // SPORT
    const sportDrilldown: Ref<DrilldownNode> = computedAsync(async () => {
        if (!event?.value) return;
        if (!event.value.drilldownNodes) {
            const drilldownNodes = await useDrilldownNodesStore().getDrilldownNodesById(
                event.value.competitionDrilldownTagId,
            );
            return drilldownNodes.find((item) => item.levelNumber === DRILLDOWN_SPORT_LEVEL_NUMBER);
        } else {
            return event.value.drilldownNodes?.find((item) => item.levelNumber === DRILLDOWN_SPORT_LEVEL_NUMBER);
        }
    });
    const sportCode: ComputedRef<Sport> = computed(() => sportDrilldown.value?.code);
    const sportName: ComputedRef<string> = computed(() => sportDrilldown.value?.name);

    // LEAGUE
    const leagueDrilldown: ComputedRef<DrilldownNode> = computed(() => {
        if (!event?.value) return;

        return event.value.drilldownNodes.find((item) => item.levelNumber === DRILLDOWN_LEAGUE_LEVEL_NUMBER);
    });
    const leagueCode: ComputedRef<string> = computed(() => leagueDrilldown.value?.code);
    const leagueName: ComputedRef<string> = computed(() => leagueDrilldown.value?.name);

    // COUNTRY
    const countryDrilldown = computedAsync<DrilldownNode>(async () => {
        if (!event?.value) return;
        if (!event.value.drilldownNodes) {
            const drilldownNodes = await useDrilldownNodesStore().getDrilldownNodesById(
                event.value.competitionDrilldownTagId,
            );
            return drilldownNodes.find((item) => item.levelNumber === DRILLDOWN_COUNTRY_LEVEL_NUMBER);
        } else {
            return event.value.drilldownNodes?.find((item) => item.levelNumber === DRILLDOWN_COUNTRY_LEVEL_NUMBER);
        }
    });
    const countryCode = computed(() => countryDrilldown.value?.code);
    const countryName = computed(() => countryDrilldown.value?.name);

    // BETRADAR
    const betradarAvailable: ComputedRef<boolean> = computed(() => {
        if (!event?.value) return false;

        return event.value.externalIds.some((item) => item.provider === "betradar");
    });

    const statsAvailable: ComputedRef<boolean> = computed(() => {
        if (!event?.value) return false;
        return event.value.externalIds.some((item) => item.provider === "stats");
    });

    const betradarMatchId: ComputedRef<string> = computed(() => {
        if (!event?.value) return "";

        const idArray = event.value.externalIds.find((item) => item.provider === "betradar")?.id.split(":") ?? [];

        return idArray[2];
    });

    const streamAvailable: ComputedRef<boolean> = computed(() => {
        return event.value.mediaProviders.some((item) => item.mediaType === MediaType.Video);
    });

    // HOME TEAM VARIABLES
    const homeName: ComputedRef<string> = computed(() => {
        if (!event?.value) return "";

        return event.value.teams.find((team) => team.side === Side.Home)?.name;
    });
    const homeCommentaryId: ComputedRef<string> = computed(() => {
        if (!event?.value) return "";

        return event.value.commentary?.participants.find((team) => team.roleCode === Side.Home)?.id;
    });

    // AWAY TEAM VARIABLES
    const awayName: ComputedRef<string> = computed(() => {
        if (!event?.value) return "";

        return event.value.teams.find((team) => team.side === Side.Away)?.name;
    });
    const awayCommentaryId: ComputedRef<string> = computed(() => {
        if (!event?.value) return "";

        return event.value.commentary?.participants.find((team) => team.roleCode === Side.Away)?.id;
    });

    return {
        name,

        sportCode,
        sportName,

        leagueCode,
        leagueName,

        countryCode,
        countryName,

        betradarAvailable,
        betradarMatchId,

        statsAvailable,

        streamAvailable,

        homeName,
        homeCommentaryId,

        awayName,
        awayCommentaryId,
    };
}
